/**
 * Search-shortcut utility module.
 *
 * Manages the configurable keyboard shortcut used to open the Quick Search
 * drawer. The default is Ctrl+S (or Cmd+S on Mac).
 *
 * Cross-component reactivity is achieved by dispatching a custom DOM event
 * on every save/reset so components that read the shortcut can stay in sync
 * without a shared React context.
 */

/** Modifier combinations supported for the search shortcut.
 *  "ctrl" means Ctrl on Windows/Linux and ⌘ on Mac (both checked via
 *  `e.ctrlKey || e.metaKey`). */
export type ShortcutModifier = 'ctrl' | 'ctrl+shift' | 'ctrl+alt';

export interface SearchShortcut {
  modifier: ShortcutModifier;
  /** Lowercase single alphanumeric character: a–z or 0–9 */
  key: string;
}

const STORAGE_KEY = 'ggc_search_shortcut';

/** Fired on `window` whenever the shortcut is saved or reset. */
export const SHORTCUT_CHANGE_EVENT = 'ggc:search-shortcut-change';

export const DEFAULT_SHORTCUT: SearchShortcut = { modifier: 'ctrl', key: 's' };

/* ── Capturing-mode gate ──────────────────────────────────────────────────
 * When the ShortcutCapture UI is active we must suppress the Header's
 * document-level keydown listener so it doesn't open the drawer while the
 * user is entering a new combo.  A module-level flag is the simplest gate
 * that avoids prop drilling or a React context. */
let _capturing = false;
export function setCapturingMode(on: boolean): void { _capturing = on; }
export function isCapturingMode(): boolean { return _capturing; }

/* ── Browser-reserved shortcuts ──────────────────────────────────────────
 * These fire at the OS/browser level and cannot be reliably intercepted
 * inside a web page. */
const BROWSER_RESERVED: Partial<Record<ShortcutModifier, readonly string[]>> = {
  ctrl: ['w', 't', 'n', 'q', 'r', 'l'],
  'ctrl+shift': ['n', 'q', 'w'],
};

/** Returns the human-readable name of the reserved action if blocked. */
export function getBrowserReservedReason(s: SearchShortcut): string | null {
  const list = BROWSER_RESERVED[s.modifier] ?? [];
  if (!list.includes(s.key)) return null;

  const labels: Record<string, string> = {
    w: 'Close tab',
    t: 'New tab',
    n: 'New window',
    q: 'Quit browser',
    r: 'Reload page',
    l: 'Focus address bar',
  };
  return labels[s.key] ?? 'Browser reserved';
}

/* ── Commonly-conflicting shortcuts ──────────────────────────────────────
 * Not browser-reserved (can still be intercepted), but heavily used in
 * text editors, forms, and common browser interactions. Allowed but the
 * UI should show a warning so the user can make an informed choice. */
const COMMONLY_CONFLICTING: Partial<Record<ShortcutModifier, readonly string[]>> = {
  ctrl: ['c', 'v', 'x', 'z', 'y', 'a', 'f', 'p', 'b', 'i', 'u', 'd', 'e', 'g', 'h', 'j', 'k', 'o'],
};

const CONFLICT_LABELS: Record<string, string> = {
  c: 'Copy',
  v: 'Paste',
  x: 'Cut',
  z: 'Undo',
  y: 'Redo',
  a: 'Select All',
  f: 'Find in page',
  p: 'Print',
  b: 'Bold (text editors)',
  i: 'Italic (text editors)',
  u: 'Underline (text editors)',
  d: 'Bookmark / duplicate line',
  e: 'Open address bar (some browsers)',
  g: 'Find next',
  h: 'History / find-replace',
  j: 'Downloads (Chrome)',
  k: 'Focus address bar (Chrome)',
  o: 'Open file',
};

/**
 * Returns a human-readable conflict description if the shortcut is commonly
 * used by the browser or text editors (but not browser-reserved). Returns
 * null when there is no known conflict.
 */
export function getCommonlyConflictingReason(s: SearchShortcut): string | null {
  const list = COMMONLY_CONFLICTING[s.modifier] ?? [];
  if (!list.includes(s.key)) return null;
  return CONFLICT_LABELS[s.key] ?? 'Common browser/editor shortcut';
}

/* ── Storage ─────────────────────────────────────────────────────────────*/

/** Read the persisted shortcut, falling back to the default. */
export function getStoredShortcut(): SearchShortcut {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SearchShortcut>;
      if (
        parsed.modifier &&
        ['ctrl', 'ctrl+shift', 'ctrl+alt'].includes(parsed.modifier) &&
        parsed.key &&
        /^[a-z0-9]$/.test(parsed.key)
      ) {
        return parsed as SearchShortcut;
      }
    }
  } catch { /* ignore */ }
  return DEFAULT_SHORTCUT;
}

/** Persist a new shortcut and notify all listeners via a custom event. */
export function saveShortcut(shortcut: SearchShortcut): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shortcut));
  } catch { /* ignore */ }
  window.dispatchEvent(
    new CustomEvent<SearchShortcut>(SHORTCUT_CHANGE_EVENT, { detail: shortcut }),
  );
}

/** Remove the persisted shortcut (restores default) and notify. */
export function resetShortcut(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(
    new CustomEvent<SearchShortcut>(SHORTCUT_CHANGE_EVENT, {
      detail: DEFAULT_SHORTCUT,
    }),
  );
}

/* ── Formatting ──────────────────────────────────────────────────────────*/

/** Human-readable label shown in tooltips, the settings UI, and the Quick
 *  Search dialog's own shortcut hint. Shows "Cmd" on macOS since `⌘` is what
 *  actually satisfies the "Ctrl" modifier there (see `matchesShortcut`) —
 *  labeling it "Ctrl" on Mac would tell users to press a key that isn't the
 *  one that works. */
export function formatShortcut(s: SearchShortcut): string {
  const parts: string[] = [isMacPlatform() ? 'Cmd' : 'Ctrl'];
  if (s.modifier === 'ctrl+shift') parts.push('Shift');
  if (s.modifier === 'ctrl+alt') parts.push('Alt');
  parts.push(s.key.toUpperCase());
  return parts.join('+');
}

/* ── Matching ────────────────────────────────────────────────────────────*/

/** True on macOS — where ⌘ (Command) should also satisfy the "Ctrl" modifier. */
function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  // navigator.platform is deprecated but still the most reliable cross-browser
  // signal for Mac detection. Fall back to userAgent for older browsers.
  return /Mac|iPod|iPhone|iPad/i.test(navigator.platform)
    || /Macintosh/i.test(navigator.userAgent);
}

/** Returns true when the given KeyboardEvent matches the shortcut.
 *
 *  - On macOS: both Ctrl AND ⌘ (Command) are accepted as the "Ctrl" modifier,
 *    so the default Ctrl+S works whether the user pressed `ctrl+s` or `⌘+s`.
 *  - On Windows/Linux: only the actual Ctrl key counts. The Windows/Super key
 *    (`e.metaKey`) is ignored — Win+S there is reserved for the OS search. */
export function matchesShortcut(e: KeyboardEvent, s: SearchShortcut): boolean {
  const ctrlPressed = isMacPlatform() ? (e.ctrlKey || e.metaKey) : e.ctrlKey;
  if (!ctrlPressed) return false;
  if (e.shiftKey !== (s.modifier === 'ctrl+shift')) return false;
  if (e.altKey !== (s.modifier === 'ctrl+alt')) return false;
  // e.key is locale/layout aware; compare lowercase to avoid caps-lock issues.
  return e.key.toLowerCase() === s.key.toLowerCase();
}
